/**
 * FreezeErrorOverlay — persistent, center-screen freeze diagnostics.
 *
 * The always-on FreezeSentinel already flashes a brief 1.8s corner toast when
 * a client freeze or server silence is detected. This overlay is the opt-in
 * "WoW Lua errors" mode: freezes become durable, centered alerts that show the
 * full attribution (top counters, longtasks, heap delta) plus the recent netcode
 * events that were in flight around the stall.
 *
 * Enabled by:
 *   - UserPrefs.dev.persistentFreezePopup (Settings -> Interface)
 *   - the `/debug` diagnostic surface (`?debug=1` / Playwright included)
 *   - URL override `?freezePopup=1` (no reload needed, but session-scoped)
 *
 * `?freezePopup=0` is the explicit escape hatch and wins even on `/debug`.
 *
 * Each alert is dismissible and stays until the user closes it. Multiple alerts
 * stack vertically so repeated freezes do not overwrite each other.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { EventBus, EVENTS } from '../../../shared/EventBus';
import {
  isThrottledFrameGap,
  type FreezeEventWire,
  type FreezeAttribution,
} from '../../../shared/diagnostics/FreezeEventTypes';
import type { NetworkTrafficEvent } from '../../network/NetworkTracer';
import { COLORS, FONTS, SPACE, RADII, FONT_WEIGHT } from '../theme';
import { storage } from '../../services/StorageService';
import { FREEZE_VISUAL_CONTROL_EVENT, type FreezeVisualControlDetail } from '../../../shared/diagnostics/FreezeVisualControl';
import { clampFreezeAlertOffset, type FreezeAlertOffset } from './freezeAlertDrag';
import { readFreezePopupEnabled, writeFreezePopupEnabled } from './freezePopupPreference';

const MAX_ALERTS = 5;
const NETCODE_WINDOW_MS = 2000;
const RECENT_TRAFFIC_COUNT = 50;

interface FreezeAlert {
  id: string;
  capturedAt: number;
  wire: FreezeEventWire;
  netcode: NetworkTrafficEvent[];
}

interface NetworkManagerLike {
  getRecentTraffic?: (count: number) => NetworkTrafficEvent[];
}

function getNetworkManager(): NetworkManagerLike | null {
  return (globalThis as unknown as { nm?: NetworkManagerLike }).nm ?? null;
}

function formatTime(ms: number): string {
  return new Date(ms).toLocaleTimeString('en-US', { hour12: false });
}

function attributionSourceLabel(source: FreezeAttribution['source']): string {
  switch (source) {
  case 'gc-suspected': return 'GC pause suspected';
  case 'js-longtask': return 'JS longtask';
  case 'raf-throttled': return 'rAF throttled by browser (window not focused)';
  case 'presentation-stall': return 'frames stopped while JS was idle (compositor/GPU present)';
  case 'unknown': return 'Unknown (see counters)';
  }
}

function gatherNetcodeEvents(capturedAt: number): NetworkTrafficEvent[] {
  const nm = getNetworkManager();
  const events: NetworkTrafficEvent[] = nm?.getRecentTraffic?.(RECENT_TRAFFIC_COUNT) ?? [];
  const start = capturedAt - NETCODE_WINDOW_MS;
  return events.filter((e) => {
    const ts = new Date(e.timestamp).getTime();
    return ts >= start && ts <= capturedAt;
  });
}

function makeAlertId(wire: FreezeEventWire): string {
  return `${wire.kind}-${wire.capturedAt}-${Math.random().toString(36).slice(2, 8)}`;
}

function isClientFreeze(alert: FreezeAlert): boolean {
  return alert.wire.kind === 'client_freeze';
}

function alertColor(alert: FreezeAlert): string {
  return isClientFreeze(alert) ? COLORS.warning : COLORS.danger;
}

function alertTitle(alert: FreezeAlert): string {
  return isClientFreeze(alert) ? 'CLIENT FREEZE' : 'SERVER SILENCE';
}

const FreezeAlertCard: React.FC<{
  alert: FreezeAlert;
  stackIndex: number;
  onClose: () => void;
  onDisable: () => void;
}> = ({ alert, stackIndex, onClose, onDisable }) => {
  const [offset, setOffset] = useState<FreezeAlertOffset>({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [copied, setCopied] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    pointer: FreezeAlertOffset;
    offset: FreezeAlertOffset;
    rect: { left: number; top: number; width: number; height: number };
  } | null>(null);
  const attr = alert.wire.attribution;
  const serverActivity = alert.wire.serverActivity;
  const color = alertColor(alert);

  useEffect(() => {
    if (!dragging) return;
    const move = (event: MouseEvent) => {
      const start = dragRef.current;
      if (!start) return;
      setOffset(clampFreezeAlertOffset(
        start.offset,
        start.rect,
        { x: event.clientX - start.pointer.x, y: event.clientY - start.pointer.y },
        { width: window.innerWidth, height: window.innerHeight },
      ));
    };
    const up = () => {
      setDragging(false);
      dragRef.current = null;
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
  }, [dragging]);

  const startDrag = (event: React.MouseEvent) => {
    if ((event.target as HTMLElement).closest('.toolwindow-button')) return;
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    event.preventDefault();
    dragRef.current = {
      pointer: { x: event.clientX, y: event.clientY },
      offset,
      rect: { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
    };
    setDragging(true);
  };

  const handleCopy = async () => {
    const payload = JSON.stringify(
      { ...alert.wire, netcode: alert.netcode },
      null,
      2,
    );
    const ok = await copyToClipboard(payload);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <div
      ref={cardRef}
      data-testid={`freeze-alert-${alert.wire.kind}`}
      data-freeze-alert-id={alert.id}
      data-dragging={dragging ? 'true' : 'false'}
      className={dragging ? 'toolwindow-dragging' : undefined}
      style={{
        pointerEvents: 'auto',
        position: 'relative',
        zIndex: dragging ? 10 : Math.max(1, MAX_ALERTS - stackIndex),
        transform: `translate3d(${offset.x}px, ${offset.y}px, 0)`,
        flexShrink: 0,
        width: 'min(560px, calc(100vw - 32px))',
        minWidth: 360,
        maxWidth: 560,
        background: COLORS.surface,
        border: `2px solid ${color}`,
        borderRadius: RADII.lg,
        boxShadow: '0 10px 40px rgba(0,0,0,0.7)',
        fontFamily: FONTS.mono,
        fontSize: 12,
        color: COLORS.textPrimary,
        overflow: 'hidden',
      }}
    >
      <div
        className="toolwindow-header"
        data-testid="freeze-alert-drag-handle"
        onMouseDown={startDrag}
        title="Drag to reposition this freeze diagnostic"
        style={{
          cursor: dragging ? 'grabbing' : 'grab',
          padding: `${SPACE.md}px ${SPACE.lg}px`,
          background: `${color}22`,
          borderBottom: `1px solid ${COLORS.borderSubtle}`,
        }}
      >
        <div className="toolwindow-title" style={{ color, fontFamily: FONTS.mono, fontSize: 12 }}>
          {alertTitle(alert)} — {alert.wire.stallMs}ms
        </div>
        <div className="toolwindow-controls" style={{ alignItems: 'center', display: 'flex', gap: SPACE.xs }}>
          <span style={{ color: COLORS.textMuted, fontSize: 10, marginRight: SPACE.sm }}>
            {formatTime(alert.capturedAt)}
          </span>
          <button
            type="button"
            className="toolwindow-button"
            data-testid="freeze-disable-popups"
            onClick={onDisable}
            aria-label="Disable freeze detail popups"
            title="Disable persistent freeze detail popups"
            style={{
              background: COLORS.surfaceSunken,
              border: `1px solid ${COLORS.borderSubtle}`,
              borderRadius: RADII.sm,
              color: COLORS.textSecondary,
              cursor: 'pointer',
              fontSize: 11,
              fontFamily: FONTS.mono,
              fontWeight: FONT_WEIGHT.medium,
              padding: `${SPACE.xs}px ${SPACE.sm}px`,
              boxShadow: `0 1px 0 ${COLORS.borderSubtle}`,
            }}
          >
            🚫 Disable popups
          </button>
          <button
            type="button"
            className="toolwindow-button"
            onClick={handleCopy}
            aria-label="Copy freeze details"
            title="Copy freeze details"
            style={{
              background: copied ? `${COLORS.success}22` : COLORS.surfaceSunken,
              border: `1px solid ${copied ? COLORS.success : COLORS.borderSubtle}`,
              borderRadius: RADII.sm,
              color: copied ? COLORS.success : COLORS.textSecondary,
              cursor: 'pointer',
              fontSize: 11,
              fontFamily: FONTS.mono,
              fontWeight: FONT_WEIGHT.medium,
              padding: `${SPACE.xs}px ${SPACE.sm}px`,
              boxShadow: `0 1px 0 ${COLORS.borderSubtle}`,
            }}
          >
            {copied ? '✅ Copied' : '📋 Copy'}
          </button>
          <button
            type="button"
            className="toolwindow-button toolwindow-close"
            onClick={onClose}
            aria-label="Close freeze alert"
            title="Close"
          >
            ×
          </button>
        </div>
      </div>

      <div style={{ padding: `${SPACE.lg}px`, display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE.sm }}>
          <div><span style={{ color: COLORS.textMuted }}>route:</span> {alert.wire.route || '—'}</div>
          <div><span style={{ color: COLORS.textMuted }}>heap:</span> {alert.wire.heapUsedMB ?? 'n/a'} MB</div>
        </div>

        {attr && (
          <div
            style={{
              background: COLORS.surfaceSunken,
              borderRadius: RADII.md,
              padding: SPACE.md,
              display: 'flex',
              flexDirection: 'column',
              gap: SPACE.xs,
            }}
          >
            <div style={{ color: COLORS.textSecondary }}>
              <span style={{ color: COLORS.textMuted }}>verdict:</span>{' '}
              {attributionSourceLabel(attr.source)}
            </div>
            <div style={{ color: COLORS.textSecondary }}>
              <span style={{ color: COLORS.textMuted }}>heap delta:</span>{' '}
              {attr.heapDeltaMB != null ? `${attr.heapDeltaMB} MB` : 'n/a'}
            </div>
            <div style={{ color: COLORS.textSecondary }}>
              <span style={{ color: COLORS.textMuted }}>longtasks:</span>{' '}
              {attr.longTasksMs.length > 0 ? attr.longTasksMs.map((m) => `${m}ms`).join(', ') : '—'}
            </div>
            <div style={{ color: COLORS.textSecondary }}>
              <span style={{ color: COLORS.textMuted }}>top counters:</span>{' '}
              {attr.topCounters.length > 0
                ? attr.topCounters.slice(0, 5).map((counter) => `${counter.key}=${counter.perSec}/s`).join(', ')
                : '—'}
            </div>
          </div>
        )}

        {serverActivity && (
          <div
            data-testid="freeze-server-activity"
            style={{
              background: COLORS.surfaceSunken,
              borderRadius: RADII.md,
              padding: SPACE.md,
              display: 'flex',
              flexDirection: 'column',
              gap: SPACE.xs,
            }}
          >
            <div style={{ color: COLORS.textSecondary }}>
              <span style={{ color: COLORS.textMuted }}>last server activity:</span>{' '}
              {serverActivity.lastSource ?? 'none'} · {alert.wire.stallMs}ms old at trip
            </div>
            <div style={{ color: COLORS.textSecondary }}>
              <span style={{ color: COLORS.textMuted }}>last heartbeat:</span>{' '}
              seq {serverActivity.lastHeartbeatSequence ?? 'n/a'} · net seq {serverActivity.lastServerSequence ?? 'n/a'}
              {' '}· prior gap {serverActivity.lastHeartbeatGapMs == null ? 'n/a' : `${Math.round(serverActivity.lastHeartbeatGapMs)}ms`}
            </div>
            <div style={{ color: COLORS.textSecondary, overflowWrap: 'anywhere' }}>
              <span style={{ color: COLORS.textMuted }}>trace:</span>{' '}
              {serverActivity.lastHeartbeatTraceId ?? 'n/a'}
            </div>
            <div style={{ color: COLORS.textSecondary }}>
              <span style={{ color: COLORS.textMuted }}>raw frame→callback:</span>{' '}
              frame {serverActivity.lastRawFrameSequence ?? 'n/a'} ·{' '}
              {serverActivity.lastRawToCallbackMs == null ? 'n/a' : `${serverActivity.lastRawToCallbackMs.toFixed(2)}ms`}
            </div>
            <div style={{ color: COLORS.textSecondary }}>
              <span style={{ color: COLORS.textMuted }}>receipts:</span>{' '}
              heartbeat {serverActivity.heartbeatReceipts} · state patch {serverActivity.statePatchReceipts}
              {' '}· binding {serverActivity.bindingGeneration}
            </div>
          </div>
        )}

        <div>
          <div style={{ color: COLORS.textMuted, marginBottom: SPACE.xs }}>
            netcode events ({alert.netcode.length} in {NETCODE_WINDOW_MS}ms window)
          </div>
          <div
            style={{
              maxHeight: 140,
              overflowY: 'auto',
              background: COLORS.surfaceSunken,
              borderRadius: RADII.md,
              padding: SPACE.sm,
              fontSize: 10,
              color: COLORS.textSecondary,
            }}
          >
            {alert.netcode.length === 0 ? (
              <div style={{ color: COLORS.textMuted }}>
                No netcode events in window. Enable packet logging with ?netobs=1 or /debug to see events here.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {alert.netcode.slice(0, 20).map((event, index) => (
                    <tr key={index}>
                      <td style={{ padding: '2px 6px 2px 0', color: event.direction === 'client->server' ? COLORS.info : COLORS.warning }}>
                        {event.direction === 'client->server' ? 'OUT' : 'IN'}
                      </td>
                      <td style={{ padding: '2px 6px', minWidth: 120 }}>{event.type}</td>
                      <td style={{ padding: '2px 0 2px 6px', textAlign: 'right' }}>{event.bytes} B</td>
                      <td style={{ padding: '2px 0 2px 12px', color: COLORS.textMuted }}>
                        {new Date(event.timestamp).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    // Fallback for older / non-secure contexts.
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

export const FreezeErrorOverlay: React.FC = () => {
  const [enabled, setEnabled] = useState(() => readFreezePopupEnabled());
  const [alerts, setAlerts] = useState<FreezeAlert[]>([]);
  const [unfocusedCount, setUnfocusedCount] = useState(0);
  const [vqaPreviewActive, setVqaPreviewActive] = useState(false);

  useEffect(() => {
    const control = (event: Event) => {
      if (!(window as any).playwrightTest) return;
      const detail = (event as CustomEvent<FreezeVisualControlDetail>).detail;
      if (detail?.clear) setAlerts([]);
      if (detail?.preview) {
        setEnabled(false);
        setVqaPreviewActive(true);
        setAlerts([{
          id: makeAlertId(detail.preview),
          capturedAt: detail.preview.capturedAt,
          wire: detail.preview,
          netcode: gatherNetcodeEvents(detail.preview.capturedAt),
        }]);
        return;
      }
      setVqaPreviewActive(false);
      setEnabled(detail?.hidden ? false : readFreezePopupEnabled());
    };
    window.addEventListener(FREEZE_VISUAL_CONTROL_EVENT, control);
    return () => window.removeEventListener(FREEZE_VISUAL_CONTROL_EVENT, control);
  }, []);

  // Sync with prefs / URL changes.
  useEffect(() => {
    setEnabled(readFreezePopupEnabled());
    const unsub = storage.subscribeUserPrefs(() => {
      // URL override wins over prefs until the page is reloaded.
      setEnabled(readFreezePopupEnabled());
    });
    return () => { unsub(); };
  }, []);

  const closeAlert = useCallback((id: string) => {
    setAlerts((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setAlerts([]);
    setUnfocusedCount(0);
  }, []);

  const disablePopups = useCallback(() => {
    writeFreezePopupEnabled(false);
    setEnabled(false);
    setVqaPreviewActive(false);
    setAlerts([]);
    setUnfocusedCount(0);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const unsub = EventBus.getInstance().on(EVENTS.SYSTEM_FREEZE, (wire: FreezeEventWire) => {
      // Containment, NOT hiding: the event is already persisted to DomainLog +
      // Freeze Log by FreezeSentinel before this fires. Only the foreground card
      // presentation is suppressed, because visible-unfocused rAF gaps are
      // dominated by browser throttling of a backgrounded window and would
      // otherwise flood the stack the moment DJ tabs back in.
      // Does NOT close BIG-FREEZE-01 — see
      // docs/incidents/2026-07-22-organic-client-freeze-reopen.md.
      //
      // The rule is the SHARED one the server records event names by, so the
      // popup and the DomainLog can never disagree about what counts. It is
      // narrower than the old inline `focusClass !== 'focused'` in two ways
      // that both fixed a suppressed real freeze: an unfocused `server_silence`
      // is a genuine server stall (focus does not throttle the server), and
      // `unknown` means focus was unreadable, not that the window was away.
      if (isThrottledFrameGap(wire)) {
        setUnfocusedCount((prev) => prev + 1);
        return;
      }
      const events = gatherNetcodeEvents(wire.capturedAt);
      const alert: FreezeAlert = {
        id: makeAlertId(wire),
        capturedAt: wire.capturedAt,
        wire,
        netcode: events,
      };
      setAlerts((prev) => [alert, ...prev].slice(0, MAX_ALERTS));
    });
    return () => { unsub(); };
  }, [enabled]);

  if ((!enabled && !vqaPreviewActive) || (alerts.length === 0 && unfocusedCount === 0)) return null;

  return (
    <div
      data-testid="freeze-error-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: alerts.length === 1 ? 'center' : 'flex-start',
        gap: SPACE.lg,
        overflowY: 'auto',
        boxSizing: 'border-box',
        padding: `${SPACE.lg}px 0 ${alerts.length > 1 ? 56 : SPACE.lg}px`,
        zIndex: 9999,
        pointerEvents: 'none',
      }}
    >
      {unfocusedCount > 0 && (
        <div
          data-testid="freeze-unfocused-chip"
          onClick={() => { setUnfocusedCount(0); }}
          style={{
            padding: '4px 8px',
            borderRadius: 4,
            background: 'rgba(40,40,40,0.85)',
            color: '#c8c8c8',
            font: '11px monospace',
            cursor: 'pointer',
            pointerEvents: 'auto',
          }}
          title="Freezes captured while the window was unfocused or hidden. Fully recorded in the Freeze Log; collapsed here so they do not obscure play."
        >
          ⏸ {unfocusedCount} unfocused freeze{unfocusedCount === 1 ? '' : 's'} — see Freeze Log
        </div>
      )}

      {alerts.map((alert, stackIndex) => (
        <FreezeAlertCard
          key={alert.id}
          alert={alert}
          stackIndex={stackIndex}
          onClose={() => closeAlert(alert.id)}
          onDisable={disablePopups}
        />
      ))}

      {alerts.length > 1 && (
        <div
          data-testid="freeze-dismiss-all"
          style={{
          pointerEvents: 'auto',
          position: 'fixed',
          bottom: SPACE.lg,
          left: '50%',
          transform: 'translateX(-50%)',
          // Must sit above every static (1..5) and dragged (10) alert. With
          // five tall cards, the old z=1 button was visibly present but card
          // packet rows intercepted every click, trapping the whole stack.
          zIndex: 20,
        }}
        >
          <button
            onClick={clearAll}
            style={{
              background: COLORS.danger,
              color: COLORS.textPrimary,
              border: 'none',
              borderRadius: RADII.md,
              padding: `${SPACE.xs}px ${SPACE.lg}px`,
              cursor: 'pointer',
              fontSize: 11,
              fontFamily: FONTS.mono,
            }}
          >
            Dismiss all ({alerts.length})
          </button>
        </div>
      )}
    </div>
  );
};
